import type { SignedInSessionResource } from "@clerk/types";
import axios from "axios";
import _ from "lodash";
import { create } from "zustand";
import type { ActivityChangeType } from "@/components/ActivityStream/ActivityStreamItem";
import type { CalculatedDiff } from "@/hooks/useActivityDiffs";
import {
  type ClassType,
  type ClassTypeWithPropertyType,
  PropertyType,
  tailwindFilterFunctions,
} from "@/tailwind/tailwindUtils";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { getUnexpiredToken } from "@/utils/utils";
import { useIframePoolStore } from "../../components/Canvas/IframePool/iframePoolStore";
import type {
  ControlsSpec,
  ControlValues,
} from "../../components/ControlEditor";

export const FILE_DOES_NOT_EXIST = "FILE_DOES_NOT_EXIST";

// Matches tempo-api so it knows to replace it with the actual storyboard id
export const SAMPLE_STORYBOARD_PLAEHOLDER = "${storyboardId}";

export interface EditorFile {
  id?: string; // Unique ID for this file

  filename: string;
  highlightLines?: {
    // Either start & end, or charPos should be set
    start?: number;
    end?: number;

    charPos?: number;
  };

  // An optional suffix to modify the file key, so that it doesn't clash with other files.
  // This is useful for when the same file is opened in multiple contexts and we want different models.
  keySuffix?: string;

  // If it's soft opened, switching to another file will replace it
  hardOpened?: boolean;
  context: EditorContext;

  // Optional - only if it has a calculated diff
  onFileContentsChanged?: (newContents: string) => void;

  // Other values for specific file modes
  diffType?: boolean;
  rawContents?: string;
  calculatedDiff?: CalculatedDiff;
  suggestedDiff?: string;

  // For streaming - can hide the original code
  hideOriginalCode?: boolean; // Hide the pre-diff code while streaming

  // For suggested diffs
  appliedDiffActivityId?: string; // When applied save the activity ID for undo

  // When saving a file, if a special activity type should be used
  saveActivityType?: ActivityChangeType;
}

export const diffEditorForFile = (file: EditorFile) => {
  if (!file) {
    return false;
  }

  return (
    Boolean(file.diffType) ||
    Boolean(file.calculatedDiff) ||
    (file.suggestedDiff !== undefined && file.suggestedDiff !== null)
  );
};

export const filesEqual = (file: EditorFile, file2: EditorFile) => {
  if (!file || !file2) {
    return false;
  }

  return (
    file.filename === file2.filename &&
    file.context === file2.context &&
    Boolean(file.calculatedDiff) === Boolean(file2.calculatedDiff) &&
    Boolean(file.rawContents) === Boolean(file2.rawContents) &&
    Boolean(file.diffType) === Boolean(file2.diffType)

    // Note - suggestedDiff is explicitly not compared as it is treated as
    // the same as the regular version of a file
    // Boolean(file.suggestedDiff) === Boolean(file2.suggestedDiff)
  );
};

// Order is important for comparison
export enum DevtoolStatus {
  NotInstalled,
  Outdated,
  Latest,
  Development,
}

export enum EditorContext {
  EngSpec = "EngSpec",
  History = "History",
  Code = "Code",
  StoryGeneration = "StoryGeneration",
}

export interface CodebaseStore {
  // State about the child project (can be cached eventually)
  // ID -> element
  treeElementLookup: { [codebaseId: string]: JsxElement };
  cssElementLookup: { [codebaseId: string]: any };
  dependencies: {
    regularDependencies: { [key: string]: string };
    devDependencies: { [key: string]: string };
  };
  conditions: any;
  lookupsCanvasId: string;
  fileToContents: {
    [file: string]: string;
  };
  storyArgValues: {
    [codebaseId: string]: Record<string, any>;
  };
  cachedControlsSpecs: {
    [codebaseId: string]: ControlsSpec;
  };
  cachedControlsValues: {
    [codebaseId: string]: ControlValues;
  };

  tailwindClasses: ClassTypeWithPropertyType[];
  propertiesForType: Record<PropertyType, ClassTypeWithPropertyType[]>;
  tailwindClassesMap: Record<string, ClassTypeWithPropertyType>;

  exampleStoryboardFile: string;

  showUnauthorizedModal: boolean;

  // Files
  filesToReload: {
    [filename: string]: boolean;
  }; // Files that have been modified and need to be reloaded
  isLoadingFiles: boolean;
  loadedFiles: {
    [filename: string]: boolean;
  }; // Files that have ever been loaded

  // Code editor
  revealLocationAt: number; // Uses position within file (takes precedence)
  revealLineNumber: number; // Uses line number
  attemptJumpToDefinition: boolean;

  // Files, in order they appear in
  editorContext: EditorContext;
  editor: {
    [context: string]: {
      editorFiles: EditorFile[];
      selectedFile: EditorFile;
    };
  };

  // For project launching
  projectLaunchHadError: boolean;
  projectLaunchRunning: boolean;
  projectLaunchEverRan: boolean;

  setProjectLaunchHadError: (hadError?: boolean) => void;
  setProjectLaunchRunning: (running?: boolean) => void;
  setProjectLaunchEverRan: (everRan?: boolean) => void;

  setAllLookups: (payload?: any) => void;
  updateLookupsBasedOnPartialResponse: (
    payload?: any,
    skipNotify?: boolean,
  ) => void;
  setTreeElementLookup: (payload: { [codebaseId: string]: JsxElement }) => void;
  setFilesLoaded: (payload?: any) => void;
  setReloadFiles: ({ files }: { files: string[] }) => void;
  revealLocation: (payload?: any) => void;
  openFile: (payload?: { file: EditorFile; jumpToTab?: boolean }) => void;
  updateFile: (file: EditorFile, updatedFields: Partial<EditorFile>) => void;
  setEngSpecFiles: (payload?: any) => void;
  clearEngSpecFiles: (payload?: any) => void;
  openStoryGenerationFiles: (payload?: any) => void;
  openHistoryFiles: (payload?: any) => void;
  clearHistoryFiles: (payload?: any) => void;
  hardOpenFile: (payload?: any) => void;
  setSelectedFile: (payload?: any) => void;
  closeFile: (payload?: any) => void;
  closeAllFiles: (payload?: any) => void;
  replaceOpenedFile: (payload?: any) => void;
  clearHighlightLinesForFile: (payload?: any) => void;
  setShowUnauthorizedModal: (payload?: any) => void;
  setFileToContents: (payload?: any) => void;
  getFileContents: ({
    canvasId,
    filename,
    supabaseJWT,
    forceReload,
  }: {
    canvasId: string;
    filename: string;
    supabaseJWT?: string | null;
    forceReload?: boolean;
  }) => Promise<string | undefined>;
  loadFile: (
    canvasId: string,
    supabaseJWT: string,
    filename: string,
    beforeChanges: boolean,
  ) => Promise<string | undefined | null>;
  loadAllNewFiles: (canvasId: string, session: SignedInSessionResource) => void;
  setEditorContext: (payload?: EditorContext) => void;
  setTailwindClasses: (classes: ClassType[]) => void;

  resetCodebaseStore: () => void;

  updateDependencies: (newDependencies: { [key: string]: string }) => void;

  updateStoryArgValues: (
    storyboardId: string,
    args: Record<string, any>,
  ) => void;
  setCachedControls: (codebaseId: string, controls: ControlsSpec) => void;
  setCachedControlsValues: (codebaseId: string, values: ControlValues) => void;
  getExpectedFilenameForStoryboard: (storyboardId: string) => string;
  setConditionsLibraries: (libraries: any[]) => void;
  getDefaultLibraryId: () => string | undefined;
}

const STORE_INIT: any = {
  // State about the child project (can be cached eventually)
  // ID -> element
  treeElementLookup: {},
  cssElementLookup: {},
  dependencies: {
    regularDependencies: {},
    devDependencies: {},
  },
  conditions: {},
  lookupsCanvasId: null,
  fileToContents: {},
  tailwindClasses: [],
  propertiesForType: {},
  tailwindClassesMap: {},

  // Props and Controls
  storyArgValues: {},
  cachedControlsSpecs: {},
  cachedControlsValues: {},

  showUnauthorizedModal: false,

  projectLaunchRunning: false,
  projectLaunchEverRan: false,
  projectLaunchHadError: false,

  // Files
  filesToReload: {},
  isLoadingFiles: false, // Whether any files are currently being loaded
  loadedFiles: {},

  // Code editor
  revealLocationAt: null,
  revealLineNumber: null,
  attemptJumpToDefinition: false,

  editorContext: EditorContext.Code,
  editor: {
    [EditorContext.EngSpec]: {
      editorFiles: [],
      selectedFile: null,
    },
    [EditorContext.History]: {
      editorFiles: [],
      selectedFile: null,
    },
    [EditorContext.Code]: {
      editorFiles: [],
      selectedFile: null,
    },
    [EditorContext.StoryGeneration]: {
      editorFiles: [
        {
          filename: "story2.tsx",
          context: EditorContext.StoryGeneration,
          keySuffix: "story-gen",
          rawContents: `
        import { Story } from "@storybook/react";
        import { Button } from "./Button";

        export default {
          title: "Button",
          component: Button,
        } as Meta;
        `,
        },
      ],
      selectedFile: null,
    },
  },
};

export const useCodebaseStore = create<CodebaseStore>((set, get) => ({
  ...STORE_INIT,

  setAllLookups: (payload): any => {
    const { lookups, canvasId } = payload;

    const currentState = get();

    const newState: Partial<CodebaseStore> = {
      treeElementLookup: { ...lookups.treeElementLookup },
      cssElementLookup: { ...lookups.cssElementLookup },
      filesToReload: { ...currentState.filesToReload },
      conditions: { ...currentState.conditions },
      loadedFiles: { ...currentState.loadedFiles },
      exampleStoryboardFile: payload?.exampleStoryboardFile,
    };

    const filteredNewConditions = Object.fromEntries(
      Object.entries(lookups.conditions).filter(([_, value]) => Boolean(value)),
    );

    newState.conditions = {
      ...newState.conditions,
      ...filteredNewConditions,
    };

    if (canvasId) {
      newState.lookupsCanvasId = canvasId;
    }

    newState.dependencies = lookups.dependencies || {};

    newState.filesToReload ??= {};
    // Reload all opened files
    if (newState.loadedFiles) {
      Object.keys(newState.loadedFiles).forEach((filename: string) => {
        newState.filesToReload![filename] = true;
      });
    }

    set(newState);
  },

  // Response is in the format {treeElementLookup, cssElementLookup}
  // If any of these aren't empty, delete all other lookups with the same filename and add the new ones
  updateLookupsBasedOnPartialResponse: ({
    treeElementLookup,
    cssElementLookup,
    dependencies,
    deletedFiles = [],
    conditions = {},
    changedFiles,
    wasInstantUpdate,
    hotReloadTs,
    isFlush,
  }): any => {
    const currentState = get();

    const toSet = {
      treeElementLookup: { ...currentState.treeElementLookup },
      cssElementLookup: { ...currentState.cssElementLookup },
      filesToReload: { ...currentState.filesToReload },
      conditions: { ...currentState.conditions },
      fileToContents: { ...currentState.fileToContents },
      editor: { ...currentState.editor },
      dependencies: { ...currentState.dependencies },
    };

    const stateAndPayloadPairs = [
      ["treeElementLookup", treeElementLookup],
      ["cssElementLookup", cssElementLookup],
    ];

    const deletedFilesSet = new Set(deletedFiles || []);

    stateAndPayloadPairs.forEach(([stateLookupKey, payload]) => {
      const changes = {};

      if (payload && Object.keys(payload).length) {
        const allFiles = new Set(
          Object.values(payload)
            .map((element: any) => element.filename)
            .concat(changedFiles || []),
        );

        allFiles.forEach((filename: string) => {
          // Reload only if the file is loaded previously
          if (currentState.loadedFiles[filename]) {
            toSet.filesToReload[filename] = true;
            toSet.filesToReload[`before-changes:${filename}`] = true;
          }
        });

        const newLookup = {};
        Object.values(toSet[stateLookupKey]).forEach((element: any) => {
          if (
            allFiles.has(element.filename) ||
            deletedFilesSet.has(element.filename)
          ) {
            changes[element.codebaseId] = null;
            return;
          }
          newLookup[element.codebaseId] = element;
        });

        Object.values(payload).forEach((element: any) => {
          newLookup[element.codebaseId] = element;
          changes[element.codebaseId] = element;
        });

        toSet[stateLookupKey] = newLookup;
      }
    });

    changedFiles?.forEach((filename: string) => {
      // Reload only if the file is loaded previously
      if (currentState.loadedFiles[filename]) {
        toSet.filesToReload[filename] = true;
        toSet.filesToReload[`before-changes:${filename}`] = true;
      }
    });

    const prevSelectedFileIndex = currentState.editor[
      EditorContext.Code
    ].editorFiles.findIndex((file) =>
      filesEqual(file, currentState.editor[EditorContext.Code].selectedFile),
    );

    let setNewSelectedFile = false;
    deletedFilesSet.forEach((filename: string) => {
      if (currentState.fileToContents[filename]) {
        delete toSet.fileToContents[filename];
      }

      if (currentState.fileToContents[`before-change:${filename}`]) {
        delete toSet.fileToContents[`before-changes:${filename}`];
      }

      const filteredEditorFiles = currentState.editor[
        EditorContext.Code
      ].editorFiles.filter((file) => file.filename !== filename);

      if (
        filteredEditorFiles.length !==
        currentState.editor[EditorContext.Code].editorFiles.length
      ) {
        toSet.editor[EditorContext.Code].editorFiles = filteredEditorFiles;
      }

      if (
        currentState.editor[EditorContext.Code].selectedFile?.filename ===
        filename
      ) {
        setNewSelectedFile = true;
      }
    });

    if (deletedFilesSet.size && setNewSelectedFile) {
      const indexToJumpTo = Math.min(
        prevSelectedFileIndex,
        currentState.editor[EditorContext.Code].editorFiles.length - 1,
      );

      if (indexToJumpTo >= 0) {
        toSet.editor[EditorContext.Code].selectedFile =
          toSet.editor[EditorContext.Code].editorFiles[indexToJumpTo];
      }
    }

    const filteredNewConditions = Object.fromEntries(
      Object.entries(conditions || {}).filter(([_, value]) => Boolean(value)),
    );

    toSet.conditions = {
      ...toSet.conditions,
      ...filteredNewConditions,
    };

    const haveNewDependencies =
      Object.keys(dependencies?.["devDependencies"] || {}).length ||
      Object.keys(dependencies?.["regularDependencies"] || {}).length;

    if (
      haveNewDependencies &&
      !_.isEqual(toSet.dependencies || {}, dependencies || {})
    ) {
      toSet.dependencies = dependencies;
    }

    set(toSet);

    // After changing, detect all storyboards that have changed based on the codebase ids in them and set them to load
    if (Object.keys(treeElementLookup || {}).length || changedFiles?.length) {
      const codebaseIds = new Set(Object.keys(treeElementLookup));
      useIframePoolStore
        .getState()
        .notifyCodebaseIdsChanged(
          codebaseIds,
          changedFiles ?? [],
          wasInstantUpdate,
          hotReloadTs,
        );
    }
  },

  setTreeElementLookup: (payload: { [codebaseId: string]: JsxElement }) => {
    set({
      treeElementLookup: payload,
    });
  },

  setEditorContext: (payload: EditorContext) => {
    set({
      editorContext: payload,
    });
  },

  setFilesLoaded: (payload) => {
    if (!payload?.loadedFiles?.length) {
      return;
    }

    const currentState = get();

    const toSet = {
      loadedFiles: { ...currentState.loadedFiles },
      filesToReload: { ...currentState.filesToReload },
    };

    payload.loadedFiles.forEach((fileReloaded: string) => {
      delete toSet.filesToReload[fileReloaded];
      toSet.loadedFiles[fileReloaded] = true;
    });

    set(toSet);
  },

  setFileToContents: (payload) => {
    set({
      fileToContents: {
        ...get().fileToContents,
        [payload.key]: payload.contents,
      },
    });
  },

  setReloadFiles: ({ files }: { files: string[] }) => {
    const toSet = {
      filesToReload: { ...get().filesToReload },
    };

    files?.forEach((file: string) => {
      toSet.filesToReload[file] = true;
    });

    set(toSet);
  },

  revealLocation: (payload) => {
    set({
      revealLocationAt: payload.revealLocationAt,
      revealLineNumber: payload.revealLineNumber,
      attemptJumpToDefinition: payload.attemptJumpToDefinition,
    });
  },

  setEngSpecFiles: (payload: { files: EditorFile[] }) => {
    const { files } = payload;

    const allEditorStates = get().editor;

    const editorState = allEditorStates[EditorContext.EngSpec];

    const openedFileInFiles =
      Boolean(editorState.selectedFile) &&
      Boolean(files?.find((file) => file.id === editorState.selectedFile.id));

    if (openedFileInFiles) {
      // Set the fallback (need to update the state)
      // @ts-expect-error TS2322
      editorState.selectedFile = files?.find(
        (file) => file.id === editorState.selectedFile.id,
      );

      // New file added, select the new file
      if (files?.length !== editorState.editorFiles?.length) {
        const existingFileIds = new Set(
          editorState.editorFiles?.map((file) => file.id) || [],
        );
        const newFile = files.find((file) => !existingFileIds.has(file.id));
        if (newFile) {
          editorState.selectedFile = newFile;
        }

        // Otherwise just keep the same file opened
      }
    } else if (files?.length) {
      // Open the last file
      editorState.selectedFile = files[files.length - 1];
    }

    // Set the new files
    editorState.editorFiles = files;

    set({
      editor: { ...allEditorStates },
    });
  },

  openStoryGenerationFiles(payload: {
    files: EditorFile[];
    selectedFilename?: string;
  }) {
    const { files, selectedFilename } = payload;

    const allEditorStates = get().editor;

    const editorState = allEditorStates[EditorContext.StoryGeneration];

    editorState.editorFiles = files;

    if (selectedFilename) {
      const selectedFile = editorState.editorFiles.find(
        (file) => file.filename === selectedFilename,
      );
      // @ts-expect-error TS2322
      editorState.selectedFile = selectedFile;
    } else {
      editorState.selectedFile =
        editorState.editorFiles[editorState.editorFiles.length - 1];
    }

    set({
      editor: { ...allEditorStates },
    });
  },
  clearEngSpecFiles: (payload: {}) => {
    // First, filter all previous activity files
    set({
      editor: {
        ...get().editor,
        [EditorContext.EngSpec]: {
          editorFiles: [],
          // @ts-expect-error TS2322
          selectedFile: null,
        },
      },
    });
  },

  openHistoryFiles: (payload: {
    calculatedDiffs: CalculatedDiff[];
    activityId: string;
  }) => {
    const { calculatedDiffs, activityId } = payload;

    const files: EditorFile[] = calculatedDiffs.map((diff) => {
      return {
        filename: diff.filePath,
        keySuffix: activityId,
        calculatedDiff: diff,
        hardOpened: true,
        context: EditorContext.History,
      };
    });

    const allEditorState = get().editor;
    const editorState = allEditorState[EditorContext.History];

    editorState.editorFiles = files;
    editorState.selectedFile =
      editorState.editorFiles[editorState.editorFiles.length - 1];

    set({
      editor: { ...allEditorState },
    });
  },

  clearHistoryFiles: (payload: {}) => {
    set({
      editor: {
        ...get().editor,
        [EditorContext.History]: {
          editorFiles: [],
          // @ts-expect-error TS2322
          selectedFile: null,
        },
      },
    });
  },

  openFile: (payload: { file: EditorFile; jumpToTab?: boolean }) => {
    const { file, jumpToTab } = payload;

    if (!file) {
      return;
    }

    const allEditorState = get().editor;

    let existingIndex = null;
    let softOpenedIndex = null;

    const editorFiles = allEditorState[file.context].editorFiles;

    for (let i = 0; i < editorFiles.length; i++) {
      const existingFile = editorFiles[i];
      if (filesEqual(file, existingFile)) {
        // @ts-expect-error TS2322
        existingIndex = i;
        break;
      }

      if (!softOpenedIndex && !existingFile.hardOpened) {
        // @ts-expect-error TS2322
        softOpenedIndex = i;
      }
    }

    // @ts-expect-error TS2538
    const existingFile = editorFiles[existingIndex];

    // Keep file hard opened if it was before
    if (existingFile) {
      if (existingFile.hardOpened) {
        file.hardOpened = true;
      }

      // @ts-expect-error TS2345
      editorFiles.splice(existingIndex, 1, file);

      if (jumpToTab) {
        allEditorState[EditorContext.Code].selectedFile =
          // @ts-expect-error TS2538
          editorFiles[existingIndex];
      } else if (
        existingFile === allEditorState[EditorContext.Code].selectedFile
      ) {
        allEditorState[EditorContext.Code].selectedFile = file;
      }

      set({ editor: { ...allEditorState } });
      return;
    }

    // Search for any soft opened files
    if (softOpenedIndex !== null) {
      editorFiles.splice(softOpenedIndex, 1, file);

      if (jumpToTab) {
        allEditorState[EditorContext.Code].selectedFile =
          editorFiles[softOpenedIndex];
      }

      set({ editor: { ...allEditorState } });
      return;
    }

    // Otherwise, add it to the end
    editorFiles.push(file);

    if (jumpToTab) {
      allEditorState[EditorContext.Code].selectedFile =
        editorFiles[editorFiles.length - 1];
    }

    set({ editor: { ...allEditorState } });
  },

  updateFile: (file: EditorFile, updatedFields: Partial<EditorFile>) => {
    const allEditorState = get().editor;

    let newFile: EditorFile, oldFile: EditorFile;
    const toSet = {
      ...allEditorState,
      [file.context]: {
        ...allEditorState[file.context],
        editorFiles: allEditorState[file.context].editorFiles.map(
          (existingFile) => {
            if (filesEqual(file, existingFile)) {
              oldFile = existingFile;
              newFile = {
                ...existingFile,
                ...updatedFields,
              };
              return newFile;
            }

            return existingFile;
          },
        ),
      },
    };

    // @ts-expect-error
    if (oldFile === allEditorState[file.context].selectedFile) {
      // @ts-expect-error
      toSet[file.context].selectedFile = newFile;
    }

    set({ editor: toSet });
  },

  hardOpenFile: (file: EditorFile) => {
    const allEditorState = get().editor;

    let existingIndex = null;

    if (!allEditorState[file.context]) {
      return;
    }

    for (let i = 0; i < allEditorState[file.context].editorFiles.length; i++) {
      const existingFile = allEditorState[file.context].editorFiles[i];
      if (filesEqual(file, existingFile)) {
        // @ts-expect-error TS2322
        existingIndex = i;
        break;
      }
    }

    if (existingIndex !== null) {
      const existingFile =
        allEditorState[file.context].editorFiles[existingIndex];
      const newFile = {
        ...existingFile,
        hardOpened: true,
      };

      allEditorState[file.context].editorFiles.splice(
        existingIndex,
        1,
        newFile,
      );
      allEditorState[file.context].selectedFile = newFile;
    } else {
      file.hardOpened = true;
      allEditorState[file.context].editorFiles.push(file);
      allEditorState[file.context].selectedFile = file;
    }

    set({ editor: { ...allEditorState } });
  },

  closeFile: (payload) => {
    const allEditorState = get().editor;
    const file: EditorFile = payload;

    // Create new copy of editor state
    const newEditorState = { ...allEditorState };
    const contextState = { ...newEditorState[file.context] };

    // Create new array of files
    contextState.editorFiles = contextState.editorFiles.filter(
      (existingFile) => !filesEqual(file, existingFile),
    );

    // Handle selected file
    if (filesEqual(file, contextState.selectedFile)) {
      // @ts-expect-error TS2322
      contextState.selectedFile = null;
      const indexToJumpTo = Math.min(contextState.editorFiles.length - 1, 0);
      if (indexToJumpTo >= 0) {
        contextState.selectedFile = contextState.editorFiles[indexToJumpTo];
      }
    }

    // Update the context in the new state
    newEditorState[file.context] = contextState;

    set({ editor: newEditorState });
  },

  closeAllFiles: () => {
    const currentState = get();

    set({
      editor: {
        ...currentState.editor,
        [currentState.editorContext]: {
          ...currentState.editor[currentState.editorContext],
          editorFiles: [],
        },
      },
    });
  },

  // This is only used for files in the code context
  replaceOpenedFile: (payload) => {
    const { originalFilename, newFilename } = payload;

    const currentState = get();

    for (
      let i = 0;
      i < currentState.editor[EditorContext.Code].editorFiles.length;
      i++
    ) {
      const existingFile =
        currentState.editor[EditorContext.Code].editorFiles[i];
      if (
        existingFile.filename === originalFilename &&
        !existingFile.diffType
      ) {
        // These each update the state
        get().closeFile(existingFile);
        get().openFile({
          file: { filename: newFilename, context: EditorContext.Code },
          jumpToTab: true,
        });
        if (existingFile.hardOpened) {
          get().hardOpenFile({
            filename: newFilename,
            context: EditorContext.Code,
          });
        }
        return;
      }
    }
  },

  clearHighlightLinesForFile: (payload) => {
    const file = payload;

    const currentState = get();

    for (
      let i = 0;
      i < currentState.editor[EditorContext.Code].editorFiles.length;
      i++
    ) {
      const existingFile =
        currentState.editor[EditorContext.Code].editorFiles[i];
      if (filesEqual(file, existingFile)) {
        file.highlightLines = null;
        break;
      }
    }

    set({
      editor: { ...currentState.editor },
    });
  },

  setSelectedFile: (payload) => {
    set((state) => ({
      editor: {
        ...state.editor,
        [state.editorContext]: {
          ...state.editor[state.editorContext],
          selectedFile: payload,
        },
      },
    }));
  },

  setProjectLaunchHadError: (payload) => {
    set({
      projectLaunchHadError: payload,
    });
  },

  setProjectLaunchRunning: (payload) => {
    set({
      projectLaunchRunning: payload,
    });
  },

  setProjectLaunchEverRan: (payload) => {
    set({
      projectLaunchEverRan: payload,
    });
  },

  setShowUnauthorizedModal: (payload) => {
    set({
      showUnauthorizedModal: payload,
    });
  },

  getFileContents: async ({
    canvasId,
    filename,
    supabaseJWT,
    forceReload = false,
  }: {
    canvasId: string;
    filename: string;
    supabaseJWT?: string | null;
    forceReload?: boolean;
  }) => {
    if (!forceReload) {
      const { fileToContents } = get();

      if (fileToContents[filename]) {
        return fileToContents[filename];
      }
    }

    if (!supabaseJWT) {
      return;
    }

    const contents = await get().loadFile(
      canvasId,
      supabaseJWT,
      filename,
      false,
    );
    return contents || undefined;
  },

  /**
   * Logic for loading files based on the files to reload variable
   */
  loadFile: async (
    canvasId: string,
    supabaseJWT: string,
    filename: string,
    beforeChanges: boolean,
  ): Promise<string | null> => {
    if (!canvasId) {
      return null;
    }

    try {
      // Webcontainers always puts the path relative to home
      // The projects directory is always top-level in home, so remove everything before that
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/file`,
        {
          params: {
            beforeChanges: beforeChanges ? "true" : null,
            filename,
          },
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      const fullFilePath = response.data.fullFilePath;
      [fullFilePath, fullFilePath.split(canvasId + "/")[1]].forEach(
        (fileToUse: string) => {
          const key = beforeChanges ? `before-changes:${fileToUse}` : fileToUse;

          if (response.data.status === 404) {
            get().setFileToContents({ key, contents: FILE_DOES_NOT_EXIST });
          } else {
            get().setFileToContents({
              key,
              contents: response.data.fileContents,
            });
          }

          get().setFilesLoaded({ loadedFiles: [key] });
        },
      );

      return response.data.fileContents;
    } catch (e) {
      console.error(e);
      return null;
    }
  },

  /**
   * Load all files that are specified to be loaded, along with
   * any that are open in the editor
   *
   * This is triggered by a use effect in the main editor side effects
   */
  loadAllNewFiles: async (
    canvasId: string,
    session: SignedInSessionResource,
  ) => {
    const {
      filesToReload,
      loadedFiles,
      isLoadingFiles,
      editor: editorState,
    } = get();

    if (isLoadingFiles) {
      return;
    }

    set({ isLoadingFiles: true });

    const allFilesToLoad = new Set(Array.from(Object.keys(filesToReload)));

    for (const context in EditorContext) {
      const contextFiles = editorState[context].editorFiles;
      for (let i = 0; i < contextFiles.length; i++) {
        if (contextFiles[i].diffType) {
          if (!loadedFiles[`before-changes:${contextFiles[i].filename}`]) {
            allFilesToLoad.add(`before-changes:${contextFiles[i].filename}`);
          }

          continue;
        }

        if (!loadedFiles[contextFiles[i].filename]) {
          allFilesToLoad.add(contextFiles[i].filename);
        }
      }
    }

    const allFilesToLoadList = Array.from(allFilesToLoad);

    if (allFilesToLoadList.length) {
      get().setFilesLoaded({ loadedFiles: allFilesToLoadList });
    }

    for (let i = 0; i < allFilesToLoadList.length; i++) {
      const filename = allFilesToLoadList[i];
      if (filename.startsWith("before-changes:")) {
        await get().loadFile(
          canvasId,
          (await getUnexpiredToken(session)) as string,
          filename.substring(15),
          true,
        );

        if (allFilesToLoad.has(filename.substring("before-changes:".length))) {
          continue;
        }

        await get().loadFile(
          canvasId,
          (await getUnexpiredToken(session)) as string,
          filename.substring(15),
          false,
        );
      } else {
        await get().loadFile(
          canvasId,
          (await getUnexpiredToken(session)) as string,
          filename,
          false,
        );
      }
    }

    set({ isLoadingFiles: false });
  },

  resetCodebaseStore: () => set({ ...STORE_INIT }),

  updateDependencies: (newDependencies) => {
    set((state) => ({
      dependencies: {
        ...state.dependencies,
        regularDependencies: {
          ...state.dependencies.regularDependencies,
          ...newDependencies,
        },
      },
    }));
  },

  setTailwindClasses: (classes: ClassTypeWithPropertyType[]) => {
    const newFilteredProperties = {};
    Object.values(PropertyType).forEach((type) => {
      // More efficient filtering for background colors specifically
      let filteredProperties: ClassTypeWithPropertyType[];
      if (type === PropertyType.BACKGROUND_COLOR) {
        // Pre-filter by prefix for better performance
        const bgClasses = classes.filter((p) => p.tailwind.startsWith("bg-"));

        filteredProperties = bgClasses.filter(tailwindFilterFunctions[type]);
      } else {
        filteredProperties = classes.filter(tailwindFilterFunctions[type]);
      }

      newFilteredProperties[type] = filteredProperties;
    });

    set({
      tailwindClasses: classes,
      tailwindClassesMap: classes.reduce(
        (acc, classType) => {
          acc[classType.tailwind] = classType;
          return acc;
        },
        {} as Record<string, ClassTypeWithPropertyType>,
      ),
      propertiesForType: newFilteredProperties as Record<
        PropertyType,
        ClassTypeWithPropertyType[]
      >,
    });
  },

  updateStoryArgValues: (storyboardId: string, args: Record<string, any>) => {
    set((state) => ({
      storyArgValues: {
        ...state.storyArgValues,
        [storyboardId]: args,
      },
    }));
  },

  setCachedControls: (codebaseId: string, controls: Record<string, any>) => {
    set((state) => ({
      cachedControlsSpecs: {
        ...state.cachedControlsSpecs,
        [codebaseId]: controls,
      },
    }));
  },

  setCachedControlsValues: (
    codebaseId: string,
    values: Record<string, any>,
  ) => {
    set((state) => ({
      cachedControlsValues: {
        ...state.cachedControlsValues,
        [codebaseId]: values,
      },
    }));
  },

  getExpectedFilenameForStoryboard: (storyboardId: string) => {
    const { exampleStoryboardFile } = get();
    if (exampleStoryboardFile) {
      return exampleStoryboardFile.replace(
        SAMPLE_STORYBOARD_PLAEHOLDER,
        storyboardId,
      );
    }

    // Best guess but likely incorrect
    return `src/tempobook/storyboards/${storyboardId}/index.tsx`;
  },

  setConditionsLibraries: (libraries: any[]) => {
    set((state) => ({
      conditions: {
        ...state.conditions,
        config: {
          ...state.conditions?.config,
          data: {
            ...state.conditions?.config?.data,
            libraries,
          },
        },
      },
    }));
  },

  getDefaultLibraryId: () => {
    const libraries = get().conditions?.config?.data?.libraries as
      | any[]
      | undefined;
    if (!libraries || libraries.length === 0) {
      return undefined;
    }
    // First try to find a library named "default"
    const defaultLib = libraries.find((lib: any) => lib.name === "default");
    if (defaultLib) {
      return defaultLib.id;
    }
    // Otherwise return the first library's ID
    return libraries[0]?.id;
  },
}));
